<template>
  <div class="tw-flex tw-flex-col tw-gap-2">
    <!-- Changed to column layout with gap -->
    <div>
      <!-- Container for button -->
      <input
        ref="fileInput"
        type="file"
        class="tw-hidden"
        multiple
        @change="handleFileUpload"
      >
      <o-tooltip
        variant="primary"
        position="right"
        :active="tooltipActive"
        :label="tooltipLabel"
        class="tw-flex-grow tw-max-w-xs"
      >
        <button
          :class="[
            'tw-flex tw-items-center tw-gap-2',
            'tw-p-3 tw-bg-white tw-shadow-sm tw-rounded-lg',
            'tw-text-[#F1583D] tw-font-medium tw-whitespace-nowrap',
            'tw-border tw-border-solid tw-border-[#DFE2E7] tw-cursor-pointer',
            tooltipActive ? 'tw-opacity-50 tw-cursor-not-allowed' : ''
          ]"
          :disabled="tooltipActive"
          @click="triggerFileInput"
        >
          <i class="fas fa-upload" />
          Upload PDF
        </button>
      </o-tooltip>
    </div>

    <!-- Feature flag check for activity extraction -->
    <div
      v-if="checkupStore.featureFlags.includes('Feature: Account Activity Extraction')"
      class="tw-flex tw-items-center tw-gap-2"
    >
      <input
        id="includeActivity"
        v-model="includeActivity"
        type="checkbox"
        class="tw-form-checkbox tw-h-4 tw-w-4 tw-text-[#F1583D] tw-rounded"
      >
      <label
        for="includeActivity"
        class="tw-text-sm tw-text-gray-600"
      >
        Extract Activity Data
      </label>
    </div>
  </div>
</template>


<script>
// import SubComponent from '../components/SubComponent'
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { newWorkbook, getWorkbookURL, createCSVZip, getZipFileURL } from '@frontend/shared/exceljs/helpers';
import { postSendInternalEmail } from '@frontend/api/emailsApi.js';
import * as Sentry from '@sentry/vue';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';

export default {
  name: 'UploadNewButton',
  components: { /* Subcomponents */ },
  props: {
    // prop: { type: String, required: true },
    // prop: String,
  },
  data() {
    return {
      includeActivity: false, // extract activity from brokerage statements
    };
  },
  computed: {
    ...mapStores(useCheckupStore, useAdvisorStore),
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
      };
    },
    tooltipLabel() {
      if (this.advisorStore.advisor?.firm?.credit_based_firm && this.advisorStore.advisor.firm.extraction_credits < 1) {
        return 'Email support@yourstake.org to add credits';
      } else {
        return `Cannot upload while another upload in progress`;
      }
    },
    tooltipActive() {
      console.log('credits', this.advisorStore.advisor?.firm?.extraction_credits);
      return (this.advisorStore.advisor?.firm?.credit_based_firm && this.advisorStore.advisor?.firm?.extraction_credits < 1);
    },
  },
  watch: { /*
    Watches an existing property
    Only runs when the watched property changes */
  },
  beforeCreate() {},
  created() {},
  mounted() {},
  updated() {},
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
      this.checkupStore.activeNotifications = {}; // clear existing message boxes when someone goes to upload new file
    },
    handleFileUpload(event) {
      const newFiles = event.target.files;
      for (const file of newFiles) {
        console.log('FILE', file);
        this.checkupStore.addFile(file, 'uploadPending');
      }
      event.target.value = ''; // to always trigger the onChange event from input components
      this.startUploadToBackend();
    },
    startUploadToBackend() {
      postSendInternalEmail(`portfolio_checkup/info/upload_sent.yaml`);
      this.checkupStore.uploadPendingFiles.forEach(file => this.sendStatement(file));
      // This will check the background task status every 2.5 seconds
      this.checkupStore.taskCheckInterval = setInterval(() => this.checkTaskStatus(), 2500);
    },
    async sendStatement(file) {
      // Sends the pdf statement to the backend and gets a background task id
      const url = '/api/v1/process/statement_upload/';

      // Prepares the file as form data so it can be sent in the request
      const formData = new FormData();
      formData.append('statement', file.fileObject);
      formData.append('include_activity', this.includeActivity); // user checkbox to extract account activity or not
      console.log('Include activity?', this.includeActivity);
      // Send the file
      const res = await fetch(url, {
        method: 'POST',
        headers: this.headers,
        body: formData,
      });

      // Handles a failed upload
      // Among other things, this should catch the 403 errors when a statement is blocked by AWS WAF rules.
      if (!res.ok) await this.handleInvalidFile(file, res);

      // TODO: kill the spinner if error getting file

      // Get the task id from the response
      const data = await res.json();
      console.log('DATA', data);
      if (!data.task_ids) return;
      const ids = Object.values(data.task_ids);
      const taskId = ids[0];
      console.log('TASK ID', taskId);
      const newFile = { ...file, taskId, uploadStatus: 'uploadStarted' };
      console.log('NEW FILE', newFile);
      this.checkupStore.updateFile(newFile);
    },
    async checkTaskStatus() {
      // Checks if the background task is done yet
      // Gets results if they are ready

      // { task_name: task_id }
      // This has been refactored so everything is handled by a single task
      // So taskIDs should always be an array of one task id
      const taskIDs = this.checkupStore.files.filter(file => file.uploadStatus === 'uploadStarted').map(file => file.taskId).filter(id => id);
      if (!taskIDs.length) return; // This should probably throw an error

      const url = `/api/v1/tasks/?task_ids=${taskIDs.join(',')}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: this.headers,
      });

      const tasks = (await res.json()).tasks;
      tasks.forEach(task => this.updateStatusDetails(task));

      const failedTasks = tasks.filter(task => task.status === 'failed');
      for (const task of failedTasks) {
        await this.handleFailedTask(task);
      }

      const incompleteTasks = tasks.filter(task => task.status !== 'complete' && task.status !== 'failed');

      if (!incompleteTasks.length) {
        clearInterval(this.checkupStore.taskCheckInterval);
        console.log('All uploads are complete or failed');
        console.log('TASKS', tasks);
        console.log('FILES', this.checkupStore.files);
        this.handleTasksComplete(tasks.filter(task => task.status === 'complete')); // All uploads are complete
      }
    },
    // Handles a failed upload
    // Among other things, this should catch the 403 errors when a statement is blocked by AWS WAF rules.
    async handleInvalidFile(file, res) {
      // Send email before throwing error
      const template = res.status === 403 ? 'upload_403' : 'upload';
      postSendInternalEmail(`portfolio_checkup/error/${template}.yaml`);

      // Throw an error with the server response details
      // Should get picked up by Sentry
      let responseBody;
      try {
        responseBody = await res.json();
      } catch (error) {
        responseBody = await res.text();
      }

      const errorContext = {
        status: res.status,
        statusText: res.statusText,
        responseBody,
      };

      const updatedFile = this.checkupStore.files.find(f => f.name === file.name);
      updatedFile.uploadStatus = 'uploadInvalid';
      this.checkupStore.updateFile(updatedFile);

      console.error('Server error while trying to upload statement', errorContext);
      throw new Error('Server error while trying to upload statement');
    },
    async handleFailedTask(task) {
      console.error('Background task failed', task);
      const updatedFile = this.checkupStore.files.find(file => file.taskId === task.id);

      if (task.meta.error) {
        const error = task.meta.error;
        // console.log(error.traceback);
        let errorMessage = '';
        try {
          // First try to access error.message (if it's an Error object)
          if (error && typeof error.message === 'string') {
            errorMessage = error.message.toLowerCase();
          }
          // If that doesn't work, try to convert the entire error to a string
          else {
            errorMessage = String(error || '').toLowerCase();
          }
        } catch (e) {
          // Fallback if anything goes wrong
          errorMessage = 'an unknown error occurred';
        }
        console.log(errorMessage);
        // user uploaded a PW protected PDF
        if (!task.meta.raw_data && (errorMessage.includes('password protected') || errorMessage.includes('encrypted') || errorMessage.includes('invalid'))) {
          updatedFile.isPasswordProtected = true; // Flag it for the UI
          updatedFile.uploadStatus = 'uploadInvalid';
          this.checkupStore.updateFile(updatedFile);
          return; // Stop further processing since it's a password-protected or invalid file
        }
        // We already get an email about this failure, so don't need Sentry
        // Sentry.captureException(new Error(`Background task ${task.id} failed: ${error.message}`));
      }
      updatedFile.uploadStatus = 'uploadFailed';
      this.checkupStore.updateFile(updatedFile);

      // Check if there is raw data from the task and if so put it in a zipfile
      if (task.meta.raw_data) {
        try {
          const zip = createCSVZip(task.meta.raw_data);
          const url = await getZipFileURL(zip);
          const zipFileData = {
            name: task.meta.statement_name + '.zip',
            url,
            document_type: task.meta.document_type,
          };

          const updatedFile = this.checkupStore.files.find(f => f.taskId === task.id);
          updatedFile.rawDataZipFile = zipFileData;
          this.checkupStore.updateFile(updatedFile);
        } catch (error) {
          console.error('Error creating zip file:', error);
          Sentry.captureException(new Error(`Error creating zip file: ${error.message}`));
        }
      }
    },
    updateStatusDetails(task) {
      if (!task.meta.status) return;
      const updatedFile = this.checkupStore.files.find(file => file.taskId === task.id);
      updatedFile.taskMetadata = task.meta.status;
      this.checkupStore.updateFile(updatedFile);
    },
    async handleTasksComplete(tasks) {
      for (const task of tasks) {
        const taskMetadata = {
          holdings_summary: task.meta.holdings_summary,
          summary_account_totals: task.meta.summary_account_totals,
          account_totals_unavailable: task.meta.account_totals_unavailable,
          missing_accounts: task.meta.missing_accounts,
          missing_tickers: task.meta.missing_tickers,
          has_activity_data: task.meta.has_activity_data,
          activity_extraction_error: task.meta.activity_extraction_error,
          optimization_id: task.meta.optimization_id,
          statement_name: task.meta.statement_name,
          portfolio_id: task.meta.portfolio_id,
        };

        // The csv holdings export is now handled in PortfolioHoldingsArea.vue for both uploads and existing portfolios
        // But we still need the csv data for the holdings sum mismatch check
        if (task.meta.csv) {
          taskMetadata.csv = JSON.parse(task.meta.csv);
        }

        if (task.meta.activity_data) {
          console.log('Found task activity data');
          taskMetadata.activity_data = JSON.parse(task.meta.activity_data);
          console.log('Got task activity data');
        }

        const updatedFile = this.checkupStore.files.find(file => file.taskId === task.id);
        updatedFile.taskMetadata = { ...updatedFile.taskMetadata, ...taskMetadata };
        updatedFile.uploadStatus = 'uploadCompleted';
        this.checkupStore.updateFile(updatedFile);

        const portfolioDetails = await this.getPortfolioDetails(taskMetadata.portfolio_id);

        this.checkupStore.addPortfolio({
          external_id: taskMetadata.portfolio_id,
          name: taskMetadata.statement_name,
          ...portfolioDetails,
        }, 'portfolio');
      };

      this.validateUploads();
    },
    async getPortfolioDetails(id) {
      const url = `/api/v1/entities/${id}/holdings`;
      const res = await fetch(url, {
        method: 'GET',
        headers: this.headers,
      });
      return (await res.json()).portfolio;
    },
    validateUploads() {
      // Loop through all the files that have finished uploading
      const uploadCompletedFiles = this.checkupStore.files.filter(file => file.uploadStatus === 'uploadCompleted');

      const notificationInfo = {
        MISSING_TICKER_SYMBOLS: { statements: [] },
        SUCCESS_MISSING_TICKER_SYMBOLS: { statements: [] },
        NO_MATCHED_HOLDINGS: { statements: [] },
        SUCCESS_NO_MATCHED_HOLDINGS: { statements: [] },
        SUCCESS_ALL: { statements: [] },
        ACCOUNT_TOTALS_UNAVAILABLE: { statements: [] },
        MISSING_ACCOUNTS: { statements: [] },
      };

      uploadCompletedFiles.forEach(file => {
        const portfolio = this.checkupStore.portfolios.find(portfolio => portfolio.external_id === file.taskMetadata.portfolio_id);
        const statementName = file.taskMetadata.statement_name;

        // Holdings sum mismatch (otherwise known as suspect accounts)
        // Sum of holdings doesn't match total in summary section
        const holdingsByAccount = this.getHoldingsByAccount(file.taskMetadata.csv);
        const summaryAccountTotals = file.taskMetadata.summary_account_totals;

        if (summaryAccountTotals) {
          const accuracyChecks = this.accuracyCheckResults(file.taskMetadata.summary_account_totals, holdingsByAccount);

          const suspectAccounts = accuracyChecks.filter(result => {
            return !result.pass;
          }).map(result => result.account);

          if (suspectAccounts?.length) {
            file.suspectAccounts = suspectAccounts;
            this.checkupStore.updateFile(file);
          }
        }

        console.log('missing ticker symbols', file.taskMetadata.missing_tickers);
        console.log('unmatched holdings', portfolio.unmatched_holdings?.length);
        // no matched holdings but otherwise successful
        if ((portfolio.holdings.length === 0 && portfolio.unmatched_holdings.length > 0) && !file.suspectAccounts && !file.taskMetadata.account_totals_unavailable && !file.taskMetadata.missing_accounts) {
          notificationInfo.SUCCESS_NO_MATCHED_HOLDINGS.statements.push(statementName);
        // no matched holdings and other issues
        } else if ((portfolio.holdings.length === 0 && portfolio.unmatched_holdings.length > 0)) {
          notificationInfo.NO_MATCHED_HOLDINGS.statements.push(statementName);
        }

        // Missing ticker symbols but everything else in extraction is successful
        // We are not actually triggering the notification yet, just collecting a list of the statements.
        if (file.taskMetadata.missing_tickers && !(portfolio.holdings.length === 0 && portfolio.unmatched_holdings.length > 0) && !file.suspectAccounts && !file.taskMetadata.account_totals_unavailable && !file.taskMetadata.missing_accounts) {
          notificationInfo.SUCCESS_MISSING_TICKER_SYMBOLS.statements.push(statementName);
        }

        // Account totals unavailable
        if (file.taskMetadata.account_totals_unavailable) {
          notificationInfo.ACCOUNT_TOTALS_UNAVAILABLE.statements.push(statementName);
        }

        // Missing accounts
        // Either summary page is missing or some accounts on summary page don't have holdings
        // TODO: Missing summary page did not trigger this in my tests (but missing holdings did)
        if (file.taskMetadata.missing_accounts) {
          notificationInfo.MISSING_ACCOUNTS.statements.push(statementName);
        }

        // SUCCESS- PDF uploaded and zero warnings
        if (!file.suspectAccounts && !file.taskMetadata.missing_tickers && !file.taskMetadata.account_totals_unavailable && !file.taskMetadata.missing_accounts) {
          notificationInfo.SUCCESS_ALL.statements.push(statementName);
        }
      });

      // Missing ticker symbols
      // Actually trigger the notification

      // for multiple uploads to avoid messiness, only show one success box ( SUCCESS_MISSING_TICKERS or SUCCESS_ALL)
      // Also show a warning box for portfolios with 0 matched holdings, indicating successful extraction but no matched tickers
      // success = no support required
      console.log('success missing tickers', notificationInfo.SUCCESS_MISSING_TICKER_SYMBOLS.statements.length);
      console.log('success no tickers', notificationInfo.SUCCESS_NO_MATCHED_HOLDINGS.statements.length);
      console.log('success all', notificationInfo.SUCCESS_ALL.statements.length);
      // if all the successes had some missing ticker symbols
      if (notificationInfo.SUCCESS_MISSING_TICKER_SYMBOLS.statements.length > 0 && notificationInfo.SUCCESS_ALL.statements.length === 0) {
        this.checkupStore.addNotification('SUCCESS_MISSING_TICKER_SYMBOLS', notificationInfo.SUCCESS_MISSING_TICKER_SYMBOLS);
      }
      // if some of the successes had missing tickers, and some did not
      if (notificationInfo.SUCCESS_MISSING_TICKER_SYMBOLS.statements.length > 0 && (notificationInfo.SUCCESS_ALL.statements.length > 0 )) {
        notificationInfo.SUCCESS_ALL.statements.forEach(statementName => {
          notificationInfo.SUCCESS_MISSING_TICKER_SYMBOLS.statements.push(statementName);
        });
        this.checkupStore.addNotification('SUCCESS_MISSING_TICKER_SYMBOLS', notificationInfo.SUCCESS_MISSING_TICKER_SYMBOLS);
      }


      // if none of the successes had missing tickers
      if (notificationInfo.SUCCESS_ALL.statements.length > 0 && notificationInfo.SUCCESS_MISSING_TICKER_SYMBOLS.statements.length === 0) {
        this.checkupStore.addNotification('SUCCESS_ALL', notificationInfo.SUCCESS_ALL);
      }
      // if any of the successes had were 100% unmatched, have a separate box to show this because analytics won't be possible for those
      if (notificationInfo.SUCCESS_NO_MATCHED_HOLDINGS.statements.length > 0) {
        this.checkupStore.addNotification('SUCCESS_NO_MATCHED_HOLDINGS', notificationInfo.SUCCESS_NO_MATCHED_HOLDINGS);
      }

      // Account totals unavailable
      if (notificationInfo.ACCOUNT_TOTALS_UNAVAILABLE.statements.length) {
        this.checkupStore.addNotification('ACCOUNT_TOTALS_UNAVAILABLE', notificationInfo.ACCOUNT_TOTALS_UNAVAILABLE);
      }

      // Missing accounts
      if (notificationInfo.MISSING_ACCOUNTS.statements.length) {
        this.checkupStore.addNotification('MISSING_ACCOUNTS', notificationInfo.MISSING_ACCOUNTS);
      }

      // Holdings sum mismatch (otherwise known as suspect accounts)
      const statementsWithSuspectAccounts = uploadCompletedFiles.filter(file => file.suspectAccounts?.length > 0).map(file => file.name);
      if (statementsWithSuspectAccounts.length) {
        this.checkupStore.addNotification('HOLDINGS_SUM_MISMATCH', { statements: statementsWithSuspectAccounts });
      }

      this.sendUploadResultEmail();

      const updatedFiles = [...this.checkupStore.files];
      updatedFiles.forEach(file => {
        if (file.uploadStatus === 'uploadCompleted') file.uploadStatus = 'uploadValidated';
      });
      this.checkupStore.files = updatedFiles;
    },
    getHoldingsByAccount(csvData) {
      const byAccount = {};

      csvData?.forEach(holding => {
        const account = holding['Account'];
        if (!account) return;
        const currency = holding['Market Value'];
        const number = this.currencyToNumber(currency);

        const current = byAccount[account];
        byAccount[account] = current ? current + number : number;
      });
      return byAccount;
    },
    accuracyCheckResults(summaryAccountTotals, holdingsByAccount) {
      // We can no longer rely on consistent account naming and account number extraction.
      // Instead, we will loop through holdings totals and check for a matching value in the summary totals.
      return Object.keys(holdingsByAccount).map(account => {
        const total = holdingsByAccount[account];
        const matchingSummaryTotals = summaryAccountTotals.filter(summary => {
          const account_total = this.currencyToNumber(summary['total_value']); // Ensure formatted as number
          const error = Math.abs(total - account_total);
          return error < 5; // Considered a match if difference is less than $5
        });

        return {
          account,
          total,
          matchingSummaryTotals,
          pass: matchingSummaryTotals.length > 0,
        };
      });
    },
    sendUploadResultEmail() {
      let template = 'success/upload';

      if (this.checkupStore.files.some(file => file.uploadStatus === 'uploadFailed')) {
        template = 'error/extraction_failure';
      } else if (this.checkupStore.activeNotifications.HOLDINGS_SUM_MISMATCH) {
        template = 'error/total_mismatch';
      }

      postSendInternalEmail(`portfolio_checkup/${template}.yaml`);
    },
    currencyToNumber(currency) {
      if (!currency) return 0;
      // If it's already a number just return it
      if (typeof currency === 'number') return currency;

      // Remove any character except for numbers and periods
      const clean = currency.replace(/[^\d.]/g, '');
      return Number(clean);
    },
  },
};
// TODO
// Things to test
// notifications
// optimizers notifications
// sectorExceedsBenchmark warning
// marketCapDeviationExists warning
// value is worse than the benchmark warning
// tooHighTrackingErrors warning
// impactfulMetricsFound warning

// Magic ticker fill
// create conversation
// tooltip for extraction

// better feedback for when click run analysis
// need loading feedback for optimizer
// notification in weird place with weird color
// needs to prepopulate benchmark

// double check email notifications

// Game plan:
// address the TODOs just for portfolio selection and upload
// Address the old upload wrapper
// drop in the configs
</script>


<style scoped>

</style>
